import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import {
  ButtonModule,
  DialogModule,
  DropdownModule,
  MessageService,
  MultiSelectModule,
  SelectItem,
  SharedModule,
  TableModule,
  TooltipModule,
} from 'primeng';
import { LazyLoadEvent } from 'primeng/api';
import { VmeCommonModule } from '../../../vme-common.module';
import { VmePipesModule } from '../../../vme-pipes.module';
import { SupplierRiskManagementService } from '../../../services/supplier-risk-management.service';
import {
  IUnscheduledTask,
  IUnscheduledTasksDTO,
} from '../../../interfaces/supplier-risk-management/unscheduled-tasks';
import { IUnscheduledTasksSearch } from '../../../interfaces/supplier-risk-management/unscheduled-tasks-search';
import { SupplierRiskManagementServiceType } from '../../../enums/supplier-risk-management/supplier-risk-management-service-type.enum';
import { AMPriorityEnum } from '../../../enums/am-priority.enum';
import { PillComponent } from '../components/pill/pill.component';
import {
  CUSTOMER_PRIORITY_OPTIONS,
  SERVICE_TYPE_OPTIONS,
  SUPPLIER_CONTRACT_TYPE_OPTIONS,
  defaultServiceTypes,
} from '../risk-management-filter-options';
import { DateTimeSelectorComponent } from '../date-time-selector/date-time-selector.component';
import { TravelSchedulerService } from '../../../services/travel-scheduler.service';
import { UpdateSupplierScheduleRequest } from '../../../interfaces/travel-scheduler/update-supplier-schedule-request';
import { Message } from '../../../classes/Message';
import { roundUpToNextHour } from '../../../utils/date-utils';

interface IUnscheduledTaskFilterValues {
  supplierIds: number[];
  supplierContractTypes: number[];
  customerIds: number[];
  priorities: number[];
  serviceTypes: SupplierRiskManagementServiceType[];
  taskStatuses: number[];
  daysAhead: number;
}

/**
 * Lists live tasks which have no driver schedule set, ordered by the latest customer pickup date.
 * These tasks cannot appear on the risks view because that view defines "today" by the driver
 * schedule, so without this list they are never chased.
 */
@Component({
  selector: 'unscheduled-tasks',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    MultiSelectModule,
    SharedModule,
    TableModule,
    TooltipModule,
    VmeCommonModule,
    VmePipesModule,
    PillComponent,
    DateTimeSelectorComponent,
  ],
  templateUrl: './unscheduled-tasks.component.html',
  styleUrls: ['./unscheduled-tasks.component.scss'],
})
export class UnscheduledTasksComponent implements OnInit {
  /**
   * Emitted when a task leaves this list because its schedule was set, so the view badge (an
   * unfiltered total) can be decremented without being affected by this view's filters.
   */
  @Output() taskScheduled = new EventEmitter<void>();

  protected data: IUnscheduledTasksDTO;
  protected isLoading: boolean = true;

  protected pageSize: number = 25;
  protected pageIndex: number = 0;

  protected filterForm: FormGroup;

  protected suppliers: SelectItem[] = [];
  protected customers: SelectItem[] = [];

  /**
   * Unlike the other lists these come from the API, because only a handful of the task statuses can
   * ever apply to work with no schedule set.
   */
  protected taskStatuses: SelectItem[] = [];

  protected readonly supplierContractTypes = SUPPLIER_CONTRACT_TYPE_OPTIONS;
  protected readonly priorities = CUSTOMER_PRIORITY_OPTIONS;
  protected readonly serviceTypes = SERVICE_TYPE_OPTIONS;

  /** Specific to this view: the risks view is always scoped to today. */
  protected readonly daysAheadOptions: SelectItem[] = [
    { value: 1, label: 'Next 1 day' },
    { value: 3, label: 'Next 3 days' },
    { value: 7, label: 'Next 7 days' },
    { value: 14, label: 'Next 14 days' },
    { value: 30, label: 'Next 30 days' },
  ];

  constructor(
    private supplierRiskManagementService: SupplierRiskManagementService,
    private travelSchedulerService: TravelSchedulerService,
    private messageService: MessageService,
  ) {
    this.initFilters();
  }

  async ngOnInit() {
    // The initial page of data is loaded by the table's first lazy load event.
    const options = await this.supplierRiskManagementService.getUnscheduledTasksFilterOptions();
    this.suppliers = options.suppliers;
    this.customers = options.customers;
    this.taskStatuses = options.taskStatuses;
  }

  private initFilters() {
    this.filterForm = new FormGroup({
      supplierIds: new FormControl<number[]>(null),
      supplierContractTypes: new FormControl<number[]>(null),
      customerIds: new FormControl<number[]>(null),
      priorities: new FormControl<number[]>(null),
      serviceTypes: new FormControl<SupplierRiskManagementServiceType[]>(defaultServiceTypes()),
      taskStatuses: new FormControl<number[]>(null),
      daysAhead: new FormControl<number>(7),
    });
  }

  protected async search() {
    this.pageIndex = 0;
    await this.loadData();
  }

  protected async resetFilters() {
    this.initFilters();
    await this.search();
  }

  /** p-table is lazy so that paging happens in the database rather than in the browser. */
  protected async loadLazy(event: LazyLoadEvent) {
    this.pageSize = event.rows;
    this.pageIndex = event.rows > 0 ? Math.floor(event.first / event.rows) : 0;
    await this.loadData();
  }

  protected async loadData() {
    this.isLoading = true;

    const values = this.filterForm.value as IUnscheduledTaskFilterValues;

    const request: IUnscheduledTasksSearch = {
      filters: {
        supplierIds: { value: values.supplierIds ?? [] },
        supplierContractTypes: { value: values.supplierContractTypes ?? [] },
        customerIds: { value: values.customerIds ?? [] },
        priorities: { value: values.priorities ?? [] },
        serviceTypes: { value: values.serviceTypes ?? defaultServiceTypes() },
        taskStatuses: { value: values.taskStatuses ?? [] },
        daysAhead: { value: values.daysAhead ?? 7 },
      },
      first: this.pageIndex * this.pageSize,
      rows: this.pageSize,
    };

    try {
      this.data = await this.supplierRiskManagementService.getUnscheduledTasks(request);

      // Setting a schedule removes a row, so the last page can empty out underneath the user.
      if (this.data.tasks.length === 0 && this.data.total > 0 && this.pageIndex > 0) {
        this.pageIndex--;
        await this.loadData();
      }
    } finally {
      this.isLoading = false;
    }
  }

  protected showDateTimeSelector: boolean = false;
  protected editingTask: IUnscheduledTask = null;
  protected isSavingSchedule: boolean = false;
  protected currentEarliestPickup: Date;
  protected currentEarliestDropoff: Date;

  /**
   * These tasks have no schedule to edit, so the selector is seeded from now rounded up to the next
   * whole hour rather than from existing driver times.
   */
  protected openScheduleEditor(task: IUnscheduledTask) {
    const now = new Date();

    this.editingTask = task;
    // Separate instances: p-calendar can mutate the date it is bound to.
    this.currentEarliestPickup = roundUpToNextHour(now);
    this.currentEarliestDropoff = roundUpToNextHour(now);
    this.showDateTimeSelector = true;
  }

  protected async saveDriverSchedule() {
    const request: UpdateSupplierScheduleRequest = {
      jobId: this.editingTask.jobId,
      taskSupplierSchedules: [
        {
          taskId: this.editingTask.taskId,
          specifiedPickUpTime: this.currentEarliestPickup,
          specifiedDropOffTime: this.currentEarliestDropoff,
        },
      ],
    };

    this.isSavingSchedule = true;
    try {
      await this.travelSchedulerService.updateSupplierSchedule(request);
      this.messageService.add(Message.Success('Schedule Saved'));
      this.showDateTimeSelector = false;
      this.editingTask = null;
      this.taskScheduled.emit();

      // The task now has a schedule, so it drops out of this list. Reload rather than splicing the
      // row out so the total and the current page stay correct.
      await this.loadData();
    } catch (error) {
      console.error(error);
      this.messageService.add(
        Message.Error('Error Saving Schedule. Refresh the page and try again.'),
      );
    } finally {
      this.isSavingSchedule = false;
    }
  }

  /**
   * Overdue pickups are the ones that need chasing right now, so they are called out separately
   * from work that is merely coming up.
   */
  protected isOverdue(task: IUnscheduledTask): boolean {
    return task.daysUntilLatestPickup < 0;
  }

  protected isDueToday(task: IUnscheduledTask): boolean {
    return task.daysUntilLatestPickup === 0;
  }

  protected dueLabel(task: IUnscheduledTask): string {
    const days = task.daysUntilLatestPickup;

    if (days < -1) {
      return `${Math.abs(days)} days overdue`;
    }
    if (days === -1) {
      return '1 day overdue';
    }
    if (days === 0) {
      return 'Today';
    }
    if (days === 1) {
      return 'Tomorrow';
    }
    return `In ${days} days`;
  }

  protected readonly AMPriorityEnum = AMPriorityEnum;
}
